import { globals } from 'config/globals';
import { DOMHelper } from 'core/DOMHelper';
import { device } from 'utils/device';
import { Velocity } from 'utils/motion/Velocity';
import { Direction } from 'utils/motion/Direction';
import { DirectionType, AxisType } from 'app/enum/Geometry';
import fastdom from 'fastdom';

/**
 * Pointer/touch tracking side of the mouse controller: position, velocity /
 * direction / axis-lock state (moveAxis), the touch-vs-mouse flagging
 * (device.cordsFromTouch + globals.lastCordsFromTouch + the -touchpointer
 * body/html classes), the opening pointer-method guess and the delayed-cords
 * ease. Event wiring stays in MouseController (Events.on registrations live
 * there; handler bodies delegate in here).
 */
export class MouseTracking {
	private _dom: DOMHelper;

	private _mouseCords: any = { x: 0, y: 0 };
	private _dragdown: boolean;
	private _touchActive: boolean = false;

	//Libraries:
	private _velocity: Velocity;
	private _direction: Direction;


	private _x: number = 0;
	private _y: number = 0;

	constructor(dom: DOMHelper) {
		this._dom = dom;
	}

	public get velocityX(): number {
		return this._velocity.x;
	}

	public get velocityY(): number {
		return this._velocity.y;
	}

	public get direction(): DirectionType {
		return this._direction.direction;
	}

	public get directionAxis(): AxisType {
		return this._direction.axis;
	}

	public get moveAxis(): AxisType {
		return this._direction.moveAxis;
	}

	/** Delayed (eased) mouse X */
	public get delayedX(): number {
		return this._x;
	}

	/** Delayed (eased) mouse Y */
	public get delayedY(): number {
		return this._y;
	}

	public get touchActive(): boolean {
		return this._touchActive;
	}

	//Init (called from MouseController.init — order preserved)
	//-------------------------------------------------------------------------------------

	public init(): void {
		this._setOpeningPointerMethodGuess();

		this._velocity = new Velocity();
		this._direction = new Direction();
	}

	private _setLastCordsFromTouch(fromTouch, source) {
		if (globals.lastCordsFromTouch && !fromTouch) {
			this._dom.removeClass(document.querySelector('body'), '-touchpointer');
			this._dom.removeClass(document.querySelector('html'), '-touchpointer');
		} else if (!globals.lastCordsFromTouch && fromTouch) {
			this._dom.addClass(document.querySelector('body'), '-touchpointer');
			this._dom.addClass(document.querySelector('html'), '-touchpointer');
		}

		globals.lastCordsFromTouch = fromTouch;
	}

	private _setOpeningPointerMethodGuess() {
		if (this._isIpadOS()) {
			this._setLastCordsFromTouch(true, '_setOpeningPointerMethodGuess() 1');
			return;
		}

		let lastCordsFromTouch =
			('ontouchstart' in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0) &&
			device.innerWidth < 1400;

		this._setLastCordsFromTouch(lastCordsFromTouch, '_setOpeningPointerMethodGuess() 2');
	}

	private _isIpadOS() {
		return navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /MacIntel/.test(navigator.platform);
	}

	//Queries
	//-------------------------------------------------------------------------------------

	/**
	 * Check is last pointer event was touch
	 * @returns
	 */
	public lastPointerWasTouch(): boolean {
		return globals.lastCordsFromTouch;
	}

	public getXVelocityAvg() {
		console.log('this._velocity.getXAvg()', this._velocity.getXAvg());
		return this._velocity.getXAvg();
	}

	public getMouseCords(): any {
		if (!this._mouseCords) {
			return { x: device.innerWidth / 2, y: device.innerHeight / 2 };
		}

		return this._mouseCords;
	}

	public mouseIsDown(): boolean {
		return this._dragdown;
	}

	/** Down-state flags (the cursor class side lives in MouseCursorView) */
	public setDragDown(isDown: boolean): void {
		this._dragdown = isDown;
		globals.touchActive = isDown;
	}

	//Per-frame / resize (called from MouseController — order preserved)
	//-------------------------------------------------------------------------------------

	public tick(): void {
		//Caluclate veloclity of mouse
		//Could be disabled in vertical drag sections if required:

		this._velocity.calculate(this._mouseCords);
		this._direction.calculate(this._mouseCords);

		this._x = this._x + ((this._mouseCords.x - this._x) / 10) * (window as any).deltaTime;
		this._y = this._y + ((this._mouseCords.y - this._y) / 10) * (window as any).deltaTime;
	}

	public resize(): void {
		this._direction.resize();
	}

	//Pointer / touch handler bodies (registrations stay in MouseController)
	//-------------------------------------------------------------------------------------


	private _flagTouch(isTouch: boolean): void {
		device.cordsFromTouch = isTouch;
		this._touchActive = isTouch;
		globals.touchActive = isTouch;
		device.cordsFromTouch = isTouch;
		this._setLastCordsFromTouch(isTouch, '_flagTouch');
	}

	public onMouseDown(e: PointerEvent): void {
		let isTouch = e.pointerType == 'touch';

		this._flagTouch(isTouch);

		this._mouseCords.x = e.clientX;
		this._mouseCords.y = e.clientY;

		if (isTouch) {
			this._addResetTimeOut();
		}

		this._direction.pointerDown({
			x: e.clientX,
			y: e.clientY
		});
	}

	public onMouseUp(e: PointerEvent): void {
		let isTouch = e.pointerType == 'touch';
		this._flagTouch(isTouch);

		if (isTouch) {
			clearTimeout(this._resetPointerCordsInt);
			setTimeout(() => {
				this._mouseCords.x = -5000;
				this._mouseCords.y = -5000;
			}, 100);
		}

		this._direction.pointerUp();
		this._touchActive = false;
	}



	private _resetPointerCordsInt;

	private _addResetTimeOut() {
		clearTimeout(this._resetPointerCordsInt);
		this._resetPointerCordsInt = setTimeout(() => {
			this._mouseCords.x = -5000;
			this._mouseCords.y = -5000;
		}, 1000);
	}

	public onMouseMove(e: PointerEvent): void {
		let isTouch = e.pointerType == 'touch';

		this._flagTouch(isTouch);

		if (isTouch) {
			this._addResetTimeOut();
		}

		fastdom.measure(() => {
			this._mouseCords.x = e.clientX;
			this._mouseCords.y = e.clientY;
		});
	}

}
